/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import type { backHandlerProps } from '@airasia-rn/ui/context';

export type SnapConfig = {
  backHandler?: (params?: backHandlerProps) => void;
  nearestAirport?: string;
  onUIReady?: () => void;
  geoId?: string;
  lfcChannelHash?: string;
  /**
   * Hotel gateway credentials the host provisions. The SNAP details page
   * forwards these to `HotelConfigProvider`, because the reused hotels view
   * fetches content and reviews from the hotel gateway itself.
   */
  hotelClientId?: string;
  hotelJwtHash?: string;
  hotelXorKey?: string;
};

type SnapConfigProviderProps = SnapConfig & {
  children: ReactNode;
};

type SnapConfigContextType = {
  config: SnapConfig;
  updateConfig: (updates: Partial<SnapConfig>) => void;
};

export const SnapConfigContext = createContext<
  SnapConfigContextType | undefined
>(undefined);

export const SnapConfigProvider: React.FC<SnapConfigProviderProps> = ({
  children,
  hotelClientId,
  hotelJwtHash,
  hotelXorKey,
  geoId,
  lfcChannelHash,
  nearestAirport,
  ...initialConfig
}) => {
  const [config, setConfig] = useState<SnapConfig>({
    ...initialConfig,
    hotelClientId,
    hotelJwtHash,
    hotelXorKey,
    geoId,
    lfcChannelHash,
    nearestAirport,
  });

  // Props are the host's source of truth for credentials. The first state
  // snapshot is not: a hot reload, or a host that resolves secrets after the
  // first commit, would otherwise leave useHotelDetail disabled forever.
  useEffect(() => {
    setConfig((current) => {
      if (
        current.hotelClientId === hotelClientId &&
        current.hotelJwtHash === hotelJwtHash &&
        current.hotelXorKey === hotelXorKey &&
        current.geoId === geoId &&
        current.lfcChannelHash === lfcChannelHash &&
        current.nearestAirport === nearestAirport
      ) {
        return current;
      }

      return {
        ...current,
        hotelClientId,
        hotelJwtHash,
        hotelXorKey,
        geoId,
        lfcChannelHash,
        nearestAirport,
      };
    });
  }, [
    geoId,
    hotelClientId,
    hotelJwtHash,
    hotelXorKey,
    lfcChannelHash,
    nearestAirport,
  ]);

  const updateConfig = useCallback((updates: Partial<SnapConfig>) => {
    setConfig((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const value = useMemo(
    () => ({
      config,
      updateConfig,
    }),
    [config, updateConfig],
  );

  return (
    <SnapConfigContext.Provider value={value}>
      {children}
    </SnapConfigContext.Provider>
  );
};

export const useSnapConfig = (): SnapConfigContextType => {
  const ctx = useContext(SnapConfigContext);
  if (!ctx)
    throw new Error('useSnapConfig must be used inside SnapConfigProvider');
  return ctx;
};

export const useOptionalSnapConfig = (): SnapConfigContextType | undefined =>
  useContext(SnapConfigContext);
